import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { IconUpload, IconTrash } from '@tabler/icons-react';
import { EventPicker } from '../components/upload/EventPicker.tsx';
import { UploadDropzone } from '../components/upload/UploadDropzone.tsx';
import { UploadProgressList } from '../components/upload/UploadProgressList.tsx';
import { GlassPanel } from '../components/ui/Glass.tsx';
import { Button } from '../components/ui/Button.tsx';
import { useUploadQueue } from '../hooks/useUploadQueue.ts';
import { validateFiles } from '../lib/validation.ts';
import { sanitizeEventName, eventPrefix, isSidecarKey } from '../lib/keys.ts';
import { listAllFiles } from '../lib/apiClient.ts';
import { indexBySize, isDuplicateContent } from '../lib/contentHash.ts';
import { UNCATEGORIZED_EVENT } from '../config/awsConfig.ts';
import { toast } from '../state/toastStore.ts';

export function UploadPage() {
  const [event, setEvent] = useState<string>(UNCATEGORIZED_EVENT);
  const { tasks, uploading, enqueue, start, retry, remove, clear } = useUploadQueue();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const pendingCount = useMemo(
    () => tasks.filter((t) => t.status === 'pending' || t.status === 'error').length,
    [tasks],
  );
  const doneCount = useMemo(() => tasks.filter((t) => t.status === 'done').length, [tasks]);

  const [checkingDupes, setCheckingDupes] = useState(false);

  const handleFiles = async (files: File[]) => {
    const { valid, errors } = validateFiles(files);
    errors.forEach((e) => toast.error(e));
    if (valid.length === 0) return;

    const targetEvent = sanitizeEventName(event) || UNCATEGORIZED_EVENT;

    // Content-based de-dup: skip files whose bytes already exist in the target
    // event (same size + same MD5/ETag). One listing call, hash only on size
    // collisions. Failures here never block the upload.
    let toEnqueue = valid;
    try {
      setCheckingDupes(true);
      const existing = (await listAllFiles({ prefix: eventPrefix(targetEvent, 'original') })).filter(
        (f) => !isSidecarKey(f.key),
      );
      const bySize = indexBySize(existing);
      const checked = await Promise.all(
        valid.map(async (f) => ({ file: f, dup: await isDuplicateContent(f, bySize) })),
      );
      const dupes = checked.filter((c) => c.dup);
      toEnqueue = checked.filter((c) => !c.dup).map((c) => c.file);
      if (dupes.length > 0) {
        toast.info(
          `Skipped ${dupes.length} file${dupes.length !== 1 ? 's' : ''} already in "${targetEvent}"`,
        );
      }
    } catch {
      // If the dup check fails (e.g. offline), fall back to uploading all.
      toEnqueue = valid;
    } finally {
      setCheckingDupes(false);
    }

    if (toEnqueue.length > 0) enqueue(toEnqueue, targetEvent);
  };

  const handleStart = async () => {
    await start();
    const failed = tasks.some((t) => t.status === 'error');
    // Refresh listings so the new photos show up in the gallery.
    await queryClient.invalidateQueries({ queryKey: ['events'] });
    await queryClient.invalidateQueries({ queryKey: ['files'] });
    if (!failed) toast.success('Upload complete');
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-fg">Upload</h1>
        <p className="mt-1 text-sm text-fg-2">
          Add photos and videos to an event. Everyone can contribute and browse.
        </p>
      </div>

      <GlassPanel className="space-y-5 p-5">
        <EventPicker value={event} onChange={setEvent} />
        <UploadDropzone onFiles={handleFiles} disabled={uploading || checkingDupes} />
        {/* Explicit target confirmation — mobile users have complained files
            landed in Uncategorized because the picker state was silently
            unset. Showing where they'll land removes any guesswork. */}
        <p className="text-center text-xs text-muted">
          Uploading to <span className="font-medium text-fg-2">{event || UNCATEGORIZED_EVENT}</span>
        </p>
        {checkingDupes && (
          <p className="text-center text-xs text-muted">Checking for duplicates…</p>
        )}
      </GlassPanel>

      {tasks.length > 0 && (
        <GlassPanel className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg-2">
              {tasks.length} file{tasks.length !== 1 ? 's' : ''}
              {doneCount > 0 && ` · ${doneCount} done`}
            </h2>
            {doneCount > 0 && (
              <button
                onClick={clear}
                className="inline-flex items-center gap-1 text-xs text-muted hover:text-fg-2"
              >
                <IconTrash className="h-3.5 w-3.5" /> Clear completed
              </button>
            )}
          </div>

          <UploadProgressList tasks={tasks} onRetry={retry} onRemove={remove} />

          <div className="flex gap-2">
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              disabled={uploading || pendingCount === 0}
              onClick={handleStart}
            >
              <IconUpload className="h-5 w-5" />
              {uploading ? 'Uploading…' : `Upload ${pendingCount} file${pendingCount !== 1 ? 's' : ''}`}
            </Button>
            {doneCount > 0 && !uploading && (
              <Button
                variant="secondary"
                size="lg"
                onClick={() =>
                  navigate(
                    `/events/${encodeURIComponent(sanitizeEventName(event) || UNCATEGORIZED_EVENT)}`,
                  )
                }
              >
                View event
              </Button>
            )}
          </div>
        </GlassPanel>
      )}
    </div>
  );
}
